import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import {
  AnalyticsAppType,
  AnalyticsModules,
  useAnalytics,
  useMixpanel,
  useSelected
} from '@setvi/shared/hooks';
import algoliasearch from 'algoliasearch';
import { Settings } from '@algolia/client-search';
import { useSnackbar } from 'notistack';
import { Category, Customer } from 'Services/Mongo/Types';
import {
  CompanyProductUserType,
  CompanySettingsTypeID
} from '@setvi/shared/services/react-query/query/user/types';
import { PriceGroup, Product } from '@setvi/shared/interfaces';
import { useSearchParams } from 'react-router-dom';
import {
  GetPricesProps,
  PriceType,
  PricingType,
  useProductsPrices
} from 'pages/products/api';
import { StringParam, useQueryParam } from 'use-query-params';
import {
  parseKeyValueArrayToObject,
  ProductActionType
} from 'pages/products/utils';
import { useCategories } from 'componentsV2/categiries/useCategories';
import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';

export const CATEGORY_FILTER = 'categories.breadcrumbs._id:';

export const getFilters = (id: Category['_id']) =>
  id ? `${CATEGORY_FILTER}${id}` : '';

export interface NormalizeFacetProps {
  facets: readonly string[];
  facetsValues: Record<string, Record<string, number>>;
}

export const sortValues = (values: FacetValue[]) =>
  values.sort((a, b) => {
    if (a.value < b.value) {
      return -1;
    }
    if (a.value > b.value) {
      return 1;
    }
    return 0;
  });

export const sortFullList = (
  name: string,
  values: FacetValue[],
  selectedFacets: string[]
) => {
  const selected: FacetValue[] = [];
  const unselected: FacetValue[] = [];
  const selectedValues = selectedFacets
    .filter(value => value.split(':')[0].includes(name))
    .map(value => {
      const isValueWithImageUrl = value.includes('https://');
      if (value.split(':')[0].includes(name))
        return isValueWithImageUrl
          ? `${value.split(':')[1]}:${value.split(':')[2]}`
          : value.split(':')[1];
      return null;
    });

  values.forEach(value => {
    if (selectedValues.includes(value.value)) {
      selected.push(value);
    } else {
      unselected.push(value);
    }
  });

  return [...sortValues(selected), ...sortValues(unselected)];
};

// FIXME: This can be removed when we extend facets state to contain parent facet with values
export const buildFacets = (facets: string[]) => {
  const groupedFacets = facets.reduce(
    (acc: { [key: string]: string[] }, curr) => {
      const [key, value] = curr.split(':');
      let val = value;

      if (key === 'custom.product_certifications.value') {
        val += `:${curr.split(':')[2]}`;
      }

      return {
        ...acc,
        [key]: acc[key] ? [...acc[key], `${key}:${val}`] : [`${key}:${val}`]
      };
    },
    {}
  );
  return Object.entries(groupedFacets).map(([key]) => groupedFacets[key]);
};

export const findCategoryRecursive = (
  categories: Category[],
  categoryId: string
): Category | null => {
  for (let i = 0; i < categories.length; i += 1) {
    if (categories[i]._id === categoryId) {
      return categories[i];
    }
    if (categories[i].children) {
      const found = findCategoryRecursive(categories[i].children, categoryId);
      if (found) return found;
    }
  }
  return null;
};

const custom = {
  _15for15: 'custom.15_for_15_product',
  countriesForSale: 'custom.countries_certified_for_sale'
};

export const customerAccountNumber = 'customerAccountNumber';
export const priceGroupId = 'priceGroupId';

interface useAlgoliaProductsProps {
  hitsPerPage?: number;
  selectedCustomer?: Customer;
  selectedPriceGroup?: PriceGroup;
  userType?: CompanyProductUserType;
  appType?: AnalyticsAppType;
  canFetchPrices?: boolean;
  fetchProductsByDefault?: boolean;
  facetFilters?: string[];
  module?: 'rfq' | 'catalog';
  importedFilters?: string;
  currProduct?: Product;
  pricingType?: PricingType;
}

export type FacetValue = {
  value: string;
  count: number;
};
export type FormattedFacet = {
  name: string;
  values: FacetValue[];
};

export const useAlgoliaProducts = ({
  hitsPerPage = 20,
  selectedCustomer,
  selectedPriceGroup,
  userType,
  canFetchPrices,
  appType,
  fetchProductsByDefault = true,
  facetFilters = [],
  module = 'catalog',
  importedFilters = '',
  currProduct,
  pricingType
}: useAlgoliaProductsProps) => {
  const { track } = useMixpanel();

  const [searchParams] = useSearchParams();
  const { companyData, user: appUser } = useAppContext();
  const { categories } = useCategories();
  const { getPrices } = useProductsPrices();
  const { captureAnalytics } = useAnalytics();

  const productAppId = useMemo(
    () =>
      companyData.Settings[CompanySettingsTypeID.AlgoliaProductAppId]?.Value,
    [companyData]
  );
  const apiKey = useMemo(
    () =>
      companyData.Settings[CompanySettingsTypeID.AlgoliaProductSearchApiKey]
        ?.Value,
    [companyData]
  );
  const productIndex = useMemo(
    () =>
      companyData.Settings[CompanySettingsTypeID.AlgoliaProductIndex]?.Value,
    [companyData]
  );
  const client = useMemo(
    () => algoliasearch(productAppId, apiKey),
    [productAppId, apiKey]
  );
  const index = useMemo(
    () => client.initIndex(productIndex),
    [client, productIndex]
  );

  const selectedCustomerAccountNumber = useMemo(
    () =>
      selectedCustomer?.accountNumber ||
      searchParams.get(customerAccountNumber),
    [searchParams, selectedCustomer?.accountNumber]
  );

  const selectedPriceGroupId = useMemo(
    () => selectedPriceGroup?._id || searchParams.get(priceGroupId),
    [searchParams, selectedPriceGroup?._id]
  );

  const priceFilterAccNumber = useMemo(
    () => selectedCustomerAccountNumber || selectedPriceGroupId,
    [selectedCustomerAccountNumber, selectedPriceGroupId]
  );

  const externalMiddlebyUser = appUser.ExternalUserDomainId !== 0;

  const { handleSelect: onSelectFacet, selected: selectedFacets } =
    useSelected<string>({
      dataKeyGetter: el => el
    });
  const [categoryId] = useQueryParam('categoryId', StringParam);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<Category>(null);
  const [filters, setFilters] = useState<string>(importedFilters || '');
  const [isLoading, setIsLoading] = useState<boolean>(!!categoryId);
  const [fetchMoreLoading, setfetchMoreLoading] = useState<boolean>(false);
  const [productPricesLoading, setProductPricesLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);
  const [facets, setFacets] = useState<FormattedFacet[]>([]);
  const [page, setPage] = useState<number>(0);
  const [settings, setSettings] = useState<Settings | undefined>();

  const { enqueueSnackbar } = useSnackbar();

  // Add a ref to store the current AbortController
  const abortControllerRef = useRef<AbortController | null>(null);

  const normalizeFacets = useCallback(
    ({ facets: facetResponse, facetsValues }: NormalizeFacetProps) => {
      // !FIXME This condition is currently solution on the FE side, should remove it and fix it on the BC side when possible
      // Hide these 2 facet filters for Middleby external users
      const list = externalMiddlebyUser
        ? facetResponse.filter(
            i => ![custom._15for15, custom.countriesForSale].includes(i)
          )
        : facetResponse;

      return list
        .map(facet => ({
          name: facet,
          values: sortValues(
            Object.keys(facetsValues[facet] || {}).map(key => ({
              value: key,
              count: facetsValues[facet][key]
            }))
          )
        }))
        .filter(({ values }) => values.length);
    },
    [externalMiddlebyUser]
  );

  const fetchProducts = useCallback(
    async ({
      query = searchQuery,
      selectedFilters = filters,
      startingPage = page,
      selectedFacetFilters = selectedFacets,
      latestSelectedFacetsKey = ''
    } = {}) => {
      const isFetchMore = startingPage > 0;

      isFetchMore ? setfetchMoreLoading(true) : setIsLoading(true);

      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create a new AbortController for this request
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      // !FIXME This condition is currently solution on the FE side, should remove it and fix it on the BC side when possible
      // Always use these 3 facet filters as default for Middleby external users
      const selectedFacetsList = externalMiddlebyUser
        ? [
            `${custom.countriesForSale}:United States`,
            `${custom.countriesForSale}:United States of America`,
            `${custom.countriesForSale}:Canada`,
            ...selectedFacetFilters
          ]
        : selectedFacetFilters;

      try {
        const response = await index.search<Product>(query, {
          hitsPerPage,
          facets: ['*'],
          facetFilters: buildFacets([...facetFilters, ...selectedFacetsList]),
          filters: selectedFilters,
          page: startingPage
        });

        // Check if the request was aborted
        if (signal.aborted) {
          return;
        }

        let prod: Product[] =
          response?.hits.map((hit: Product) => ({
            ...hit,
            name: toTitleCase(hit.name),
            ...(hit.vendor && {
              vendor: {
                ...hit.vendor,
                name: toTitleCase(hit.vendor.name)
              }
            }),
            price:
              userType === CompanyProductUserType.Manufacturer
                ? hit.price
                : null,
            productCost: hit.cost
          })) || [];

        if (!!prod.length && canFetchPrices && priceFilterAccNumber) {
          const prices = await getPrices({
            type: selectedPriceGroupId ? PriceType.PriceGroup : PriceType.Price,
            productList: response.hits,
            accountNumber: priceFilterAccNumber,
            ...(pricingType && { pricingType })
          });

          // Check if the request was aborted after the second API call
          if (signal.aborted) {
            return;
          }

          prod =
            response?.hits.map((hit: Product) => {
              const product = prices.find(i => i.sku === hit.sku);
              return {
                ...hit,
                name: toTitleCase(hit.name),
                ...(hit.vendor && {
                  vendor: {
                    ...hit.vendor,
                    name: toTitleCase(hit.vendor.name)
                  }
                }),
                price: product ? product.price : null,
                ...(typeof product?.cost === 'number'
                  ? { cost: product.cost }
                  : {}),
                isEditable: product?.isEditable
              };
            }) || [];
        }

        // Check if the request was aborted before updating state
        if (signal.aborted) {
          return;
        }

        setProducts(prevProducts => [...prevProducts, ...prod]);
        setPage(p => p + 1);
        setIsLastPage(response.hits.length < hitsPerPage);

        const normalaziedFacets = normalizeFacets({
          facets: response.renderingContent.facetOrdering.facets.order,
          facetsValues: response.facets
        });

        setFacets(prevFacets =>
          // FIXME: It should be better if we keep the whole facets in the state with label an value
          normalaziedFacets.map(normFacet => {
            if (
              selectedFacetFilters?.length &&
              prevFacets?.length &&
              normFacet?.name === latestSelectedFacetsKey
            ) {
              const lastSelected = prevFacets.find(
                prevFacet => prevFacet.name === latestSelectedFacetsKey
              );

              return {
                ...lastSelected,
                values: sortFullList(
                  lastSelected?.name,
                  lastSelected?.values,
                  selectedFacetsList
                )
              };
            }
            return normFacet;
          })
        );
      } catch (e) {
        // Only show error if it's not an abort error
        if (e.name !== 'AbortError') {
          enqueueSnackbar('Error occurred while searching products', {
            variant: 'error',
            autoHideDuration: 8000
          });
        }
      } finally {
        // Only update loading state if the request wasn't aborted
        if (!signal.aborted) {
          setIsLoading(false);
          setfetchMoreLoading(false);
        }
      }
    },
    [
      searchQuery,
      filters,
      page,
      selectedFacets,
      externalMiddlebyUser,
      index,
      hitsPerPage,
      facetFilters,
      canFetchPrices,
      priceFilterAccNumber,
      normalizeFacets,
      userType,
      getPrices,
      selectedPriceGroupId,
      pricingType,
      enqueueSnackbar
    ]
  );

  const getIndexSettings = useCallback(() => {
    index.getSettings().then(setSettings);
  }, [index]);

  const searchProducts = async (query: string) => {
    setSelectedCategory(null);
    setFilters(importedFilters);
    onSelectFacet(false, selectedFacets);
    setSearchQuery(query);
    setPage(0);
    setProducts([]);
    fetchProducts({
      query,
      selectedFilters: '',
      startingPage: 0,
      selectedFacetFilters: []
    });

    const analyticsFilters = parseKeyValueArrayToObject(selectedFacets);

    if (query?.trim()) {
      track({ name: 'Search', props: { page: 'Catalog', query } });

      const catalogBody = {
        actionType: ProductActionType.Search,
        searchValue: query,
        ...(selectedFacets?.length && { filters: analyticsFilters }),
        ...(selectedCustomer?._id ? { customerId: selectedCustomer?._id } : {})
      };

      const rfqBody = {
        action: 'search',
        query,
        id: currProduct?._id
      };

      captureAnalytics(
        {
          ...(module === 'rfq' ? rfqBody : catalogBody),
          appType
        },
        module === 'rfq'
          ? AnalyticsModules.AutoRFQCatalog
          : AnalyticsModules.Catalog
      );
    }
  };

  const fetchMore = () => {
    if (isLastPage) return;

    const latestSelectedFacetsKey =
      selectedFacets[selectedFacets.length - 1]?.split(':')?.[0] || '';

    fetchProducts({ latestSelectedFacetsKey });
  };

  const fetchProductsByFacet = (isChecked: boolean, facet: string[]) => {
    onSelectFacet(isChecked, facet);

    const currentFacets = isChecked
      ? [...selectedFacets, ...facet]
      : selectedFacets.filter(f => !facet.includes(f));

    setPage(0);
    setProducts([]);
    fetchProducts({
      startingPage: 0,
      selectedFacetFilters: currentFacets,
      latestSelectedFacetsKey:
        facet.length === 1 && !!currentFacets.length
          ? facet[0]?.split(':')[0]
          : ''
    });
    if (currentFacets?.length) {
      const analyticsFilters = parseKeyValueArrayToObject(currentFacets);

      captureAnalytics(
        {
          actionType: ProductActionType.FiltersSelection,
          appType,
          ...(searchQuery?.trim()?.length && { searchValue: searchQuery }),
          ...(currentFacets?.length && { filters: analyticsFilters }),
          ...(selectedCustomer?._id
            ? { customerId: selectedCustomer?._id }
            : {})
        },
        AnalyticsModules.Catalog
      );
    }
  };

  const handleGetAlgoliaProductPrice = useCallback(
    async (props: GetPricesProps) => {
      setProductPricesLoading(true);
      const prices = await getPrices(props);

      setProducts(prevProducts =>
        prevProducts.map(item => {
          const obtainedPrice = prices?.find(i => i.sku === item.sku)?.price;
          const obtainedCost = prices?.find(i => i.sku === item.sku)?.cost;
          const obtainedIsEditable = prices?.find(
            i => i.sku === item.sku
            // @ts-ignore Incorrect response type on getPrices API
          )?.isEditable;

          return {
            ...item,
            ...(typeof obtainedPrice === 'number'
              ? { price: obtainedPrice }
              : {}),
            ...(typeof obtainedCost === 'number'
              ? { customerCost: obtainedCost }
              : {}),
            ...(typeof obtainedIsEditable === 'boolean'
              ? {
                  isEditable: obtainedIsEditable
                }
              : {})
          };
        })
      );
      setProductPricesLoading(false);
    },
    [getPrices]
  );

  const handleUpdateAlgoliaProduct = useCallback(
    (product: Product, newValues: Partial<Product>) => {
      setProducts(prevProducts =>
        prevProducts.map(prod =>
          prod._id === product._id ? { ...prod, ...newValues } : prod
        )
      );
    },
    []
  );

  const getInitialFacets = useCallback(async () => {
    const response = await index.search('', {
      hitsPerPage,
      facets: ['*']
    });
    const normalaziedFacets = normalizeFacets({
      facets: response.renderingContent.facetOrdering.facets.order,
      facetsValues: response.facets
    });
    setFacets(normalaziedFacets);
  }, [hitsPerPage, index, normalizeFacets]);

  const fetchProductsByCategory = (id: Category['_id']) => {
    // Currently if user click on "All Categories" in the breadcrumb list, we clear the selected category state and fetch init values for products, facets and settings
    if (id === null) {
      setSelectedCategory(null);
      getInitialFacets();
      getIndexSettings();
      setProducts([]);
      setFilters(importedFilters);
      setSearchQuery('');
      setPage(0);
      fetchProducts({
        query: '',
        selectedFilters: '',
        startingPage: 0
      });
      return;
    }

    // FIXME: find better way to get category

    const category = findCategoryRecursive(categories, id);
    const categoryFilter = category?._id ? `${CATEGORY_FILTER}${id}` : '';
    const newFilters = `${importedFilters} ${categoryFilter?.trim() ? `${importedFilters?.trim() ? 'AND' : ''} ${categoryFilter}` : ''}`;

    setSelectedCategory(category);
    setFilters(newFilters);
    setPage(0);
    setProducts([]);
    setSearchQuery('');
    fetchProducts({
      selectedFilters: newFilters,
      query: '',
      startingPage: 0
    });

    captureAnalytics(
      {
        appType,
        actionType: ProductActionType.CategorySelection,
        searchValue: '',
        categoryId: id
      },
      AnalyticsModules.Catalog
    );
  };

  const refetchProducts = () => {
    setPage(0);
    setProducts([]);
    fetchProducts({
      startingPage: 0
    });
  };

  useEffect(() => {
    getIndexSettings();
    if (!categoryId && fetchProductsByDefault) fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (categoryId && categories?.length) fetchProductsByCategory(categoryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, categoryId]);

  useEffect(() => {
    setProducts(prevProducts =>
      prevProducts.map(item => ({ ...item, price: null as number | null }))
    );
  }, [selectedCustomer]);

  // Clean up the AbortController when the component unmounts
  useEffect(
    () => () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    []
  );

  return {
    products: priceFilterAccNumber
      ? products.map(i => ({ ...i, cost: i.customerCost || i.cost }))
      : products,
    searchQuery,
    attributesForFaceting: settings?.attributesForFaceting || [],
    productPricesLoading,
    isLoading,
    fetchMoreLoading,
    facets,
    selectedFacets,
    selectedCategory,
    getPrices,
    setProducts,
    setSearchQuery,
    searchProducts,
    setSelectedCategory,
    fetchProductsByCategory,
    fetchMore,
    refetchProducts,
    setProductPricesLoading,
    onSelectFacet: fetchProductsByFacet,
    handleGetAlgoliaProductPrice,
    handleUpdateAlgoliaProduct
  };
};
